import { useParams, useNavigate } from 'react-router-dom';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { ArrowLeft, MapPin, Compass, Info, Users, Lock, ChevronRight, Gamepad2, Home } from 'lucide-react';
import { getSettlement, getSettlementLocations } from '../config/midgardData';
import { getLocationIcon, getLocationTypeLabel } from '../config/worldUtils';
import { usePresence, getSettlementPresence, formatPresenceText } from '../hooks/usePresence';

/**
 * SettlementPage - Local map view of a settlement
 * 
 * Uses the new detailed artwork map with responsive hotspots.
 * Shows individual locations within a settlement that users can visit.
 */

// Settlement-specific map configurations
const SETTLEMENT_MAPS = {
  thornhaven: {
    image: '/maps/thornhaven-map.png',
    // Location positions as percentages - precisely calibrated to match the artwork
    // Verified on desktop, mobile portrait, and tablet viewports
    positions: {
      'thornhaven-square': { x: 50, y: 55 },
      'golden-mead-hall': { x: 50, y: 38 },
      'merchants-row': { x: 62, y: 50 },
      'iron-forge-smithy': { x: 38, y: 52 },
      'hearthstone-district': { x: 55, y: 65 },
      'stables': { x: 38, y: 62 },
      'northern-gate': { x: 50, y: 25 },
      'west-gate': { x: 25, y: 50 },
      'east-gate': { x: 75, y: 50 },
      'wishwell-plaza': { x: 55, y: 72 },
      'river-shrine': { x: 42, y: 30 },
      'elders-grove': { x: 30, y: 70 },
    }
  }
};

export default function SettlementPage() {
  const { settlementId } = useParams();
  const navigate = useNavigate();
  const [settlement, setSettlement] = useState(null);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [showInfo, setShowInfo] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  
  // Fetch real-time presence data
  const { presence } = usePresence(true);
  
  // Get presence count for this settlement
  const settlementPresenceCount = useMemo(() => {
    if (!settlementId) return 0;
    return getSettlementPresence(presence, settlementId);
  }, [settlementId, presence]);
  
  // Get presence count for selected location
  const locationPresenceCount = useMemo(() => {
    if (!selectedLocation) return 0;
    const locationSceneId = `${settlementId}-${selectedLocation.id}`;
    return getSettlementPresence(presence, locationSceneId);
  }, [selectedLocation, settlementId, presence]);

  useEffect(() => {
    const foundSettlement = getSettlement(settlementId);
    if (foundSettlement && foundSettlement.unlocked) {
      setSettlement(foundSettlement);
      setLocations(getSettlementLocations(settlementId));
    } else {
      navigate('/world/midgard');
    }
  }, [settlementId, navigate]);

  const handleBack = () => {
    navigate('/world/midgard');
  };

  const handleEnterLocation = useCallback(() => {
    if (selectedLocation && selectedLocation.unlocked) {
      navigate(`/world/midgard/settlement/${settlementId}/location/${selectedLocation.id}`);
    }
  }, [selectedLocation, settlementId, navigate]);

  // Enter the 3D game world at this settlement
  const handleEnter3DWorld = () => {
    navigate(`/world/midgard/play/${settlementId}`);
  };

  const handleMapClick = useCallback(() => {
    setSelectedLocation(null);
  }, []);

  if (!settlement) {
    return (
      <div className="mg-stage bg-black flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-amber-900/30 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }

  // Get map configuration for this settlement
  const mapConfig = SETTLEMENT_MAPS[settlementId];
  const useImageMap = mapConfig && mapConfig.image;

  return (
    <div 
      className="mg-stage"
      style={{ backgroundColor: '#0a0a0a' }}
      data-testid="settlement-page"
      onClick={handleMapClick}
    >
      {/* Themed Background */}
      <div className={`absolute inset-0 bg-gradient-to-br ${settlement.theme.bgGradient} opacity-50`} />

      {/* Header */}
      <header className="absolute top-0 left-0 right-0 z-20 p-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/home')}
            className="p-2 rounded-full bg-black/50 backdrop-blur-sm border border-amber-900/30 hover:border-amber-500/50 transition-all"
            data-testid="back-to-social"
            title="Return to Social Feed"
          >
            <Home className="w-5 h-5 text-amber-100" />
          </button>
          <button
            onClick={handleBack}
            className="flex items-center gap-2 px-4 py-2 rounded-full bg-black/50 backdrop-blur-sm border border-amber-900/30 hover:border-amber-500/50 transition-all"
            data-testid="back-to-region"
          >
            <ArrowLeft className="w-5 h-5 text-amber-100" />
            <span className="text-amber-100 text-sm hidden sm:inline">Midgard</span>
          </button>
        </div>

        <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-black/50 backdrop-blur-sm border border-amber-900/30">
          <Compass className="w-5 h-5" style={{ color: settlement.theme.primary }} />
          <span className="font-heading text-amber-100 text-sm uppercase tracking-wider">{settlement.name}</span>
        </div>

        <button
          onClick={(e) => { e.stopPropagation(); setShowInfo(!showInfo); }}
          className="p-2 rounded-full bg-black/50 backdrop-blur-sm border border-amber-900/30 hover:border-amber-500/50 transition-all"
          data-testid="settlement-info-btn"
        >
          <Info className="w-5 h-5 text-amber-100" />
        </button>
      </header>

      {/* Enter 3D World Button */}
      <div className="absolute top-20 left-1/2 -translate-x-1/2 z-30">
        <button
          onClick={handleEnter3DWorld}
          className="flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-amber-600 to-amber-700 text-white font-semibold shadow-lg shadow-amber-900/30 hover:from-amber-500 hover:to-amber-600 transition-all hover:scale-105"
          data-testid="enter-3d-world-btn"
        >
          <Gamepad2 className="w-5 h-5" />
          <span>Enter 3D World</span>
        </button>
      </div>

      {/* Info Panel */}
      {showInfo && (
        <div 
          className="absolute top-20 right-4 z-30 w-72 p-4 rounded-xl bg-black/80 backdrop-blur-md border border-amber-900/30 animate-fadeIn"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center gap-2 mb-2">
            <MapPin className="w-4 h-4" style={{ color: settlement.theme.primary }} />
            <span className="text-xs uppercase tracking-wider" style={{ color: settlement.theme.primary }}>
              {settlement.type} • {settlement.population}
            </span>
          </div>
          <h3 className="font-heading text-amber-100 text-lg mb-2">{settlement.name}</h3>
          <p className="text-sm text-amber-100/70 leading-relaxed">
            {settlement.description}
          </p>
          <div className="mt-3 pt-3 border-t border-amber-900/30">
            <p className="text-xs text-amber-500/70">
              {locations.filter(l => l.unlocked).length} of {locations.length} locations accessible
            </p>
          </div>
        </div>
      )}

      {/* Main Content - Local Map */}
      <div className="absolute inset-0 flex items-center justify-center p-4 pt-32 pb-8">
        <div className="w-full max-w-5xl h-full flex flex-col lg:flex-row gap-4">
          
          {/* Map Area - Now uses artwork image */}
          <div className="flex-1 relative rounded-2xl overflow-hidden border-2 border-amber-900/30 min-h-[400px]">
            {useImageMap ? (
              <>
                {/* Map Image */}
                <img
                  src={mapConfig.image}
                  alt={`Map of ${settlement.name}`}
                  className={`w-full h-full object-contain transition-opacity duration-500 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
                  onLoad={() => setImageLoaded(true)}
                  draggable={false}
                />
                
                {/* Loading placeholder */}
                {!imageLoaded && (
                  <div className="absolute inset-0 flex items-center justify-center bg-stone-900">
                    <div className="animate-pulse text-amber-200">Loading map...</div>
                  </div>
                )}

                {/* Location markers overlaid on the image */}
                {imageLoaded && locations.map((location) => {
                  const Icon = getLocationIcon(location.type);
                  const isSelected = selectedLocation?.id === location.id;
                  const position = mapConfig.positions?.[location.id] || location.position;
                  
                  if (!position) return null;
                  
                  return (
                    <button
                      key={location.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (location.unlocked) setSelectedLocation(location);
                      }}
                      disabled={!location.unlocked}
                      className={`
                        absolute transform -translate-x-1/2 -translate-y-1/2 
                        transition-all duration-200 group
                        ${location.unlocked ? 'cursor-pointer' : 'cursor-not-allowed'}
                        ${isSelected ? 'z-20 scale-110' : 'z-10 hover:scale-105'}
                      `}
                      style={{
                        left: `${position.x}%`,
                        top: `${position.y}%`,
                      }}
                      data-testid={`location-marker-${location.id}`}
                    >
                      {/* Marker */}
                      <div className={`
                        relative w-10 h-10 sm:w-12 sm:h-12 rounded-xl 
                        flex items-center justify-center border-2 transition-all
                        ${isSelected
                          ? 'border-white bg-white/20 shadow-lg'
                          : location.unlocked
                            ? 'border-amber-600/60 bg-black/70 group-hover:border-amber-400'
                            : 'border-gray-600/40 bg-gray-800/60 opacity-50'
                        }
                      `}
                        style={isSelected ? { borderColor: settlement.theme.primary, boxShadow: `0 0 20px ${settlement.theme.primary}40` } : {}}
                      >
                        {location.unlocked ? (
                          <Icon className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: isSelected ? settlement.theme.primary : '#fbbf24' }} />
                        ) : (
                          <Lock className="w-4 h-4 text-gray-500" />
                        )}
                      </div>
                      
                      {/* Name tooltip on hover */}
                      <div className={`
                        absolute top-full mt-1 whitespace-nowrap px-2 py-1 rounded
                        bg-black/90 text-amber-100 text-xs font-medium
                        transition-opacity duration-200 pointer-events-none
                        ${isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}
                      `}>
                        {location.name}
                        {!location.unlocked && <span className="text-gray-400 ml-1">(Locked)</span>}
                      </div>
                    </button>
                  );
                })}
              </>
            ) : (
              /* Fallback: Schematic map for other settlements */
              <>
                <div className="absolute inset-0 bg-gradient-to-br from-stone-900 via-stone-800 to-stone-900" />
                <svg className="absolute inset-0 w-full h-full opacity-20" viewBox="0 0 100 100">
                  <line x1="50" y1="0" x2="50" y2="100" stroke="#8b7355" strokeWidth="2" />
                  <line x1="0" y1="50" x2="100" y2="50" stroke="#8b7355" strokeWidth="2" />
                </svg>
                
                {locations.map((location) => {
                  const Icon = getLocationIcon(location.type);
                  const isSelected = selectedLocation?.id === location.id;
                  
                  return (
                    <button
                      key={location.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (location.unlocked) setSelectedLocation(location);
                      }}
                      disabled={!location.unlocked}
                      className={`absolute transform -translate-x-1/2 -translate-y-1/2 transition-all duration-200 group ${
                        location.unlocked ? 'cursor-pointer' : 'cursor-not-allowed'
                      } ${isSelected ? 'z-20 scale-110' : 'z-10 hover:scale-105'}`}
                      style={{
                        left: `${location.position.x}%`,
                        top: `${location.position.y}%`,
                      }}
                      data-testid={`location-marker-${location.id}`}
                    >
                      <div className={`relative w-12 h-12 rounded-xl flex items-center justify-center border-2 transition-all ${
                        isSelected
                          ? 'border-white bg-white/20 shadow-lg'
                          : location.unlocked
                            ? 'border-amber-600/60 bg-black/60 group-hover:border-amber-400'
                            : 'border-gray-600/40 bg-gray-800/60 opacity-50'
                      }`}>
                        {location.unlocked ? (
                          <Icon className="w-5 h-5" style={{ color: isSelected ? settlement.theme.primary : '#fbbf24' }} />
                        ) : (
                          <Lock className="w-4 h-4 text-gray-500" />
                        )}
                      </div>
                    </button>
                  );
                })}
              </>
            )}

            {/* Settlement name label */}
            <div className="absolute bottom-4 left-4 px-3 py-1 rounded-lg bg-black/60 backdrop-blur-sm">
              <span className="text-sm text-amber-100/70 font-medium">{settlement.name}</span>
            </div>
          </div>

          {/* Right Panel - Locations List */}
          <div className="w-full lg:w-80 p-4 rounded-xl bg-black/60 backdrop-blur-md border border-amber-900/30">
            <h3 className="text-sm uppercase tracking-wider text-amber-500 mb-4 flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Locations
            </h3>
            
            <div className="space-y-2 max-h-[50vh] overflow-y-auto">
              {locations.map((location) => {
                const Icon = getLocationIcon(location.type);
                const isSelected = selectedLocation?.id === location.id;
                
                return (
                  <button
                    key={location.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (location.unlocked) setSelectedLocation(location);
                    }}
                    disabled={!location.unlocked}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg transition-all text-left ${
                      isSelected
                        ? 'bg-amber-900/40 border border-amber-500/50'
                        : location.unlocked
                          ? 'bg-white/5 hover:bg-white/10 border border-transparent'
                          : 'bg-gray-800/30 opacity-50 border border-transparent'
                    }`}
                    data-testid={`location-list-${location.id}`}
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      location.unlocked ? 'bg-amber-900/50' : 'bg-gray-700/50'
                    }`}>
                      {location.unlocked ? (
                        <Icon className="w-5 h-5 text-amber-400" />
                      ) : (
                        <Lock className="w-4 h-4 text-gray-500" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-amber-100 truncate">{location.name}</p>
                      <p className="text-xs text-amber-100/50">{getLocationTypeLabel(location.type)}</p>
                    </div>
                    {location.unlocked && (
                      <ChevronRight className="w-4 h-4 text-amber-100/30" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Selected Location Panel */}
      {selectedLocation && (
        <div 
          className="absolute bottom-4 left-1/2 -translate-x-1/2 z-30 w-full max-w-md p-4 rounded-xl bg-black/80 backdrop-blur-md border border-amber-900/30 animate-slideUp"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start justify-between mb-3">
            <div>
              <div className="flex items-center gap-2 text-xs text-amber-500 uppercase tracking-wider mb-1">
                {(() => {
                  const Icon = getLocationIcon(selectedLocation.type);
                  return <Icon className="w-3 h-3" />;
                })()}
                {getLocationTypeLabel(selectedLocation.type)}
              </div>
              <h3 className="font-heading text-xl text-amber-100">{selectedLocation.name}</h3>
            </div>
            <button
              onClick={() => setSelectedLocation(null)}
              className="p-1 rounded-full hover:bg-white/10 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 text-amber-100/50 rotate-45" />
            </button>
          </div>
          
          <p className="text-sm text-amber-100/70 mb-4">
            {selectedLocation.description}
          </p>
          
          <div className="flex items-center justify-between">
            {/* Presence info */}
            <div className="flex items-center gap-2 text-sm">
              {locationPresenceCount > 0 ? (
                <>
                  <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                  <span className="text-green-400">{formatPresenceText(locationPresenceCount)}</span>
                </>
              ) : (
                <span className="text-amber-100/50">Empty</span>
              )}
            </div>
            
            {/* Enter button */}
            <button
              onClick={handleEnterLocation}
              disabled={!selectedLocation.unlocked}
              className={`
                flex items-center gap-2 px-4 py-2 rounded-lg transition-all
                ${selectedLocation.unlocked
                  ? 'bg-amber-600 hover:bg-amber-500 text-white'
                  : 'bg-gray-700 text-gray-400 cursor-not-allowed'
                }
              `}
            >
              {selectedLocation.unlocked ? (
                <>
                  <span>Enter</span>
                  <ChevronRight className="w-4 h-4" />
                </>
              ) : (
                <>
                  <Lock className="w-4 h-4" />
                  <span>Locked</span>
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Styles */}
      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translate(-50%, 20px);
          }
          to {
            opacity: 1;
            transform: translate(-50%, 0);
          }
        }
        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}
